import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { authService } from '@/lib/api/services'
import type { LoginRequest, RegisterRequest, User } from '@/lib/api/types'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (credentials: LoginRequest) => Promise<void>
  register: (data: RegisterRequest) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  hasPermission: (permission: string) => boolean
  hasRole: (role: string) => boolean
  hasAnyRole: (roles: string[]) => boolean
  hasAllRoles: (roles: string[]) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          const userData = await authService.getCurrentUser()
          if (userData) {
            // Try to fetch full user data from API
            const { userService } = await import('@/lib/api/services')
            const currentUser = await userService.getCurrentUser()
            setUser(currentUser)
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error)
        // Clear invalid tokens
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user')
      } finally {
        setIsLoading(false)
      }
    }

    initAuth()
  }, [])

  const login = useCallback(async (credentials: LoginRequest) => {
    try {
      await authService.login(credentials)

      // Fetch full user data
      const { userService } = await import('@/lib/api/services')
      const userData = await userService.getCurrentUser()
      setUser(userData)

      // Redirect to dashboard
      navigate('/dashboard')
    } catch (error) {
      throw error
    }
  }, [navigate])

  const register = useCallback(async (data: RegisterRequest) => {
    try {
      await authService.register(data)

      // Auto-login after registration
      await login({ email: data.email, password: data.password })
    } catch (error) {
      throw error
    }
  }, [login])

  const logout = useCallback(async () => {
    try {
      await authService.logout()
      setUser(null)
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
      // Even if API call fails, clear local state
      setUser(null)
      navigate('/login')
    }
  }, [navigate])

  const refreshUser = useCallback(async () => {
    try {
      const { userService } = await import('@/lib/api/services')
      const userData = await userService.getCurrentUser()
      setUser(userData)
    } catch (error) {
      console.error('Failed to refresh user:', error)
    }
  }, [])

  const hasPermission = useCallback((permission: string): boolean => {
    if (!user) return false
    // Super admins have all permissions
    if (user.isSuperAdmin) return true
    if (!user.roles) return false
    return user.roles.some(role =>
      role.permissions?.some(p => p.name === permission)
    )
  }, [user])

  const hasRole = useCallback((role: string): boolean => {
    if (!user || !user.roles) return false
    return user.roles.some(r => r.name === role)
  }, [user])

  const hasAnyRole = useCallback((roles: string[]): boolean => {
    if (!user) return false
    return roles.some(role => hasRole(role))
  }, [user, hasRole])

  const hasAllRoles = useCallback((roles: string[]): boolean => {
    if (!user) return false
    return roles.every(role => hasRole(role))
  }, [user, hasRole])

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshUser,
    hasPermission,
    hasRole,
    hasAnyRole,
    hasAllRoles,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// HOC for protecting pages
export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
  options?: {
    redirectTo?: string
    permissions?: string[]
    roles?: string[]
    requireAll?: boolean
  }
) {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading, hasPermission, hasAnyRole, hasAllRoles } = useAuth()
    const navigate = useNavigate()

    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        navigate(options?.redirectTo || '/login')
      }

      if (!isLoading && isAuthenticated && options) {
        let hasAccess = true

        if (options.permissions) {
          hasAccess = options.requireAll
            ? options.permissions.every(p => hasPermission(p))
            : options.permissions.some(p => hasPermission(p))
        }

        if (hasAccess && options.roles) {
          hasAccess = options.requireAll
            ? hasAllRoles(options.roles)
            : hasAnyRole(options.roles)
        }

        if (!hasAccess) {
          navigate('/unauthorized')
        }
      }
    }, [isLoading, isAuthenticated, hasPermission, hasAnyRole, hasAllRoles, navigate])

    if (isLoading) {
      return <div>Loading...</div>
    }

    if (!isAuthenticated) {
      return null
    }

    return <Component {...props} />
  }
}
